/* eslint-disable no-console */
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { closeIncomingCallNotifications } from '@/services/push';
import { FEATURE_FLAGS, TWILIO_DIAL_CALL_STATUS_MESSAGE, TWILIO_SYSTEM } from '@bariendo/lib/constants';
import { TWILIO_CALL_HISTORY_EXTENSION } from '@bariendo/lib/extensions';
import { getCallInfoByPhone } from '@bariendo/lib/phone-catalog';
import {
  ConferenceControlAction,
  ConferenceParticipantRole,
  ConferenceParticipantStatus,
  type ConferenceControlResult,
  type ConferenceStartAddTarget,
  isGuestParticipant,
  type TwilioConferenceSession,
} from '@bariendo/lib/twilio-conference-session';
import { useInterval } from '@mantine/hooks';
import { createReference, getExtension, getReferenceString } from '@medplum/core';
import { Communication } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { Call, Device } from '@twilio/voice-sdk';
import { ReactElement, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { AccountPhoneNumber, getTwilioToken, setupDevice } from '../services/twilio';
import { CallNotificationActionOptions, TwilioPhoneContext } from './contexts';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';

export interface IncomingCall {
  connection: Call;
  from: string;
  callerOrganization?: string;
  callerName?: string;
}

export interface OutgoingCall {
  connection: Call;
  to: string;
  callerOrganization?: string;
  callerName?: string;
}

/** Banner/call UI snapshot survives spurious SDK disconnect after guest conference join. */
export interface ActiveCallUi {
  direction: 'incoming' | 'outgoing';
  phoneNumber: string;
  callerName?: string;
  callerOrganization?: string;
  callLegSid?: string;
}

// Cross-tab communication message types
interface CrossTabMessage {
  type:
    | 'call-answered'
    | 'call-rejected'
    | 'call-disconnected'
    | 'incoming-call'
    | 'making-call'
    | 'phone-connected'
    | 'phone-disconnected'
    | 'request-phone-status';
  /** Set by broadcastToOtherTabs so the originating tab can ignore its own message. */
  tabId?: string;
  payload?: {
    from?: string;
    isOutgoing?: boolean;
    callStartTime?: string;
  };
}

interface TwilioPhoneProviderProps {
  children: React.ReactNode;
}

function getCallLegStatus(conn: Call | null | undefined): string | undefined {
  try {
    return conn?.status?.();
  } catch {
    return undefined;
  }
}

function isCallLegClosed(conn: Call | null | undefined): boolean {
  return getCallLegStatus(conn) === 'closed';
}

/** Ignore spurious disconnect/closed signals right after accept (conference guest join). */
const POST_ANSWER_SIGNAL_GRACE_MS = 3000;

function isWithinPostAnswerGrace(answeredAtMs: number | null): boolean {
  return answeredAtMs != null && Date.now() - answeredAtMs < POST_ANSWER_SIGNAL_GRACE_MS;
}

function isTransientConferencePollingError(error: string | undefined): boolean {
  if (!error) {
    return false;
  }
  return (
    error === 'Call not found' ||
    error === 'Unauthorized' ||
    error.includes('Too Many Requests') ||
    error.includes('Precondition Failed')
  );
}

function isTransientConferenceActionError(error: string | undefined): boolean {
  if (!error) {
    return false;
  }
  return error.includes('Precondition Failed') || error.includes('Too Many Requests');
}

function resolveStableCallLegSid(conn: Call): string | undefined {
  const parentSid =
    (conn.parameters?.ParentCallSid as string | undefined) ?? conn.customParameters?.get('ParentCallSid');
  const clientSid = conn.parameters?.CallSid as string | undefined;
  return parentSid ?? clientSid;
}

/**
 * True when an Accept / Reject from a push names a call that is not the one ringing in this tab,
 * and closes that push. The push can outlive its call (a busy Device never sees the invite, and a
 * guest's push can land after they answered), and acting on it would answer or drop another call.
 * Actions without a sid (older pushes, the in-tab buttons) are never stale.
 */
function isStaleNotificationAction(
  notificationCallSid: string | undefined,
  incomingCall: IncomingCall | null
): boolean {
  if (!notificationCallSid) {
    return false;
  }
  const ringingSid = incomingCall ? resolveStableCallLegSid(incomingCall.connection) : undefined;
  if (ringingSid === notificationCallSid) {
    return false;
  }
  void closeIncomingCallNotifications(notificationCallSid);
  return true;
}

function resolveConferenceControlCallLegSid(params: {
  session: TwilioConferenceSession | null | undefined;
  practitionerRef: string | undefined;
  connection: Call | null | undefined;
  isOutgoing: boolean;
  callLegSidRef: string | null;
  activeCallUi: ActiveCallUi | null | undefined;
}): string | undefined {
  const { session, practitionerRef, connection, isOutgoing, callLegSidRef, activeCallUi } = params;

  if (session && practitionerRef) {
    const guestParticipant = session.participants.find(
      (p) =>
        isGuestParticipant(p) &&
        p.providerReference === practitionerRef &&
        p.status !== ConferenceParticipantStatus.DISCONNECTED
    );
    if (guestParticipant?.callSid) {
      return guestParticipant.callSid;
    }

    const isHost = session.participants.some(
      (p) => p.role === ConferenceParticipantRole.AGENT && p.providerReference === practitionerRef
    );
    if (isHost && session.parentCallSid) {
      return session.parentCallSid;
    }
  }

  const parentSid =
    (connection?.parameters?.ParentCallSid as string | undefined) ?? connection?.customParameters?.get('ParentCallSid');
  if (!isOutgoing && parentSid) {
    return parentSid;
  }

  if (callLegSidRef) {
    return callLegSidRef;
  }

  const clientSid = connection?.parameters?.CallSid as string | undefined;
  if (clientSid) {
    return clientSid;
  }

  return activeCallUi?.callLegSid ?? undefined;
}

// Helper to capture call quality warnings and report to Sentry
async function reportCallWarning(
  conn: Call,
  warningName: string,
  warningData: unknown,
  direction: 'incoming' | 'outgoing'
): Promise<void> {
  try {
    // getStats() exists at runtime but types are incomplete
    const stats = await (conn as any).getStats();

    const diagnostics = {
      warning: warningName,
      warningData,
      direction,
      callSid: conn.parameters?.CallSid,
      stats: stats
        ? {
            bytesSent: stats.totals?.bytesSent,
            bytesReceived: stats.totals?.bytesReceived,
            packetsSent: stats.totals?.packetsSent,
            packetsReceived: stats.totals?.packetsReceived,
            packetsLost: stats.totals?.packetsLost,
            packetsLostFraction: stats.totals?.packetsLostFraction,
            mos: stats.mos,
            rtt: stats.rtt,
            jitter: stats.jitter,
            codecName: stats.codecName,
          }
        : null,
    };

    console.warn('Call quality warning:', diagnostics);

    Sentry.captureMessage(`Twilio call warning: ${warningName}`, {
      level: warningName === 'low-bytes-sent' || warningName === 'low-bytes-received' ? 'error' : 'warning',
      tags: {
        'twilio.warning': warningName,
        'twilio.direction': direction,
        'twilio.codec': stats?.codecName ?? 'unknown',
      },
      extra: diagnostics,
    });
  } catch (error) {
    console.error('Failed to report call warning:', error);
  }
}

export function TwilioPhoneProvider({ children }: TwilioPhoneProviderProps): ReactElement {
  const medplum = useMedplum();

  const [device, setDevice] = useState<Device | null>(null);
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [outgoingCall, setOutgoingCall] = useState<OutgoingCall | null>(null);
  const [isOutgoingCall, setIsOutgoingCall] = useState<boolean>(false);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [isInCallFromOtherTab, setIsInCallFromOtherTab] = useState<boolean>(false);
  const [availableCallerIds, setAvailableCallerIds] = useState<AccountPhoneNumber[]>([]);
  const [conferenceSession, setConferenceSession] = useState<TwilioConferenceSession | null>(null);
  const [conferenceSessionError, setConferenceSessionError] = useState<string | null>(null);
  const [canManageConference, setCanManageConference] = useState(false);
  const [activeCallUi, setActiveCallUi] = useState<ActiveCallUi | null>(null);
  const [isRemotePartyConnected, setIsRemotePartyConnected] = useState(false);

  const isMultiPartyCallingEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_MULTI_PARTY_CALLING);

  // This is used to force a refresh of the connection status
  const [refreshConnectionStatus, setRefreshConnectionStatus] = useState<number>(1);

  // Ref to track the current device to avoid stale closure issues during async operations
  const deviceRef = useRef<Device | null>(null);
  const currentCallRef = useRef<Call | null>(null);
  const callAnsweredAtRef = useRef<number | null>(null);
  const callLegSidRef = useRef<string | null>(null);
  const conferenceSessionRef = useRef<TwilioConferenceSession | null>(null);
  const conferenceRefreshSeqRef = useRef(0);
  const conferenceRefreshInFlightRef = useRef(false);

  /** Set when the provider ends an outgoing call from the UI so we don't show an "unanswered" notice. */
  const outgoingHangupInitiatedLocallyRef = useRef(false);
  const patientLeftNotifiedRef = useRef(false);

  // Keep ref in sync with device state
  useEffect(() => {
    deviceRef.current = device;
  }, [device]);

  useEffect(() => {
    currentCallRef.current = currentCall;
  }, [currentCall]);

  useEffect(() => {
    conferenceSessionRef.current = conferenceSession;
  }, [conferenceSession]);

  const resetState = useCallback(() => {
    setCurrentCall(null);
    setIncomingCall(null);
    setOutgoingCall(null);
    setCallStartTime(null);
    setIsOutgoingCall(false);
    setIsInCallFromOtherTab(false);
    setActiveCallUi(null);
    setConferenceSession(null);
    setConferenceSessionError(null);
    setCanManageConference(false);
    setIsRemotePartyConnected(false);
    currentCallRef.current = null;
    callAnsweredAtRef.current = null;
    callLegSidRef.current = null;
    patientLeftNotifiedRef.current = false;
  }, []);

  const getActiveCallLegSid = useCallback((): string | undefined => {
    const profile = medplum.getProfile();
    const practitionerRef = profile ? getReferenceString(createReference(profile)) : undefined;

    return resolveConferenceControlCallLegSid({
      session: conferenceSession,
      practitionerRef,
      connection: currentCall,
      isOutgoing: isOutgoingCall,
      callLegSidRef: callLegSidRef.current,
      activeCallUi,
    });
  }, [conferenceSession, currentCall, activeCallUi, isOutgoingCall, medplum]);

  // Cross-tab communication channel
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const tabIdRef = useRef(crypto.randomUUID());
  const hasRequestedInitialPhoneStatusAtRef = useRef<boolean>(false);
  const hasReceivedPhoneStatusFromOtherTabsAtRef = useRef<boolean>(false);

  // Helper function to broadcast messages to other tabs
  const broadcastToOtherTabs = useCallback((message: CrossTabMessage) => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ ...message, tabId: tabIdRef.current });
    }
  }, []);

  const registerDevice = useCallback(async () => {
    if (device?.state === Device.State.Unregistered) {
      try {
        await device.register();
        setDevice(device);
      } catch (err) {
        console.warn('Failed to register Twilio device:', err);
      }
    }
    setRefreshConnectionStatus((s) => s + 1);
  }, [device]);

  const unregisterDevice = useCallback(async () => {
    if (device && device.state === Device.State.Registered) {
      device.disconnectAll();
      await device.unregister();
    }
    setRefreshConnectionStatus((s) => s + 1);
  }, [device]);

  // Shared reconnection logic with timeout and polling
  // Used when we need to ensure device is registered before proceeding (e.g., making a call)
  const reconnectDeviceWithTimeout = useCallback(async (timeoutMs = 5000): Promise<Device | null> => {
    // Use ref to get the latest device, avoiding stale closure issues
    const currentDevice = deviceRef.current;

    if (!currentDevice) {
      alert.error('Phone service unavailable. Please refresh the page.');
      return null;
    }

    if (currentDevice.state === Device.State.Registered) {
      return currentDevice;
    }

    // Show reconnection notification
    alert.info('Reconnecting to phone service...');

    try {
      const startTime = Date.now();

      // Register the device if it's unregistered
      if (currentDevice.state === Device.State.Unregistered) {
        await currentDevice.register();
      }

      // Wait for device to register (polling with timeout)
      // Always check the ref to get the latest device, in case it was updated during polling
      while (deviceRef.current?.state !== Device.State.Registered && Date.now() - startTime < timeoutMs) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        // Re-check device from ref in case it changed during polling
        const latestDevice = deviceRef.current;
        if (latestDevice && latestDevice.state === Device.State.Unregistered) {
          await latestDevice.register();
        }
      }

      // Get the final device state from ref
      const finalDevice = deviceRef.current;
      if (!finalDevice || finalDevice.state !== Device.State.Registered) {
        alert.error('Unable to connect to phone service. Please refresh the page.');
        return null;
      }

      setDevice(finalDevice);
      setRefreshConnectionStatus((s) => s + 1);

      console.log('Device reconnected successfully');
      return finalDevice;
    } catch (error) {
      console.error('Failed to reconnect device:', error);
      alert.error('Unable to connect to phone service. Please refresh the page.');
      return null;
    }
  }, []);

  const connect = useCallback(
    async (broadcast = true) => {
      console.log('Connecting to Twilio phone');

      await registerDevice();
      if (broadcast) {
        broadcastToOtherTabs({ type: 'phone-connected' });
      }
    },
    [broadcastToOtherTabs, registerDevice]
  );

  const disconnect = useCallback(
    async (broadcast = true) => {
      await unregisterDevice();
      if (broadcast) {
        broadcastToOtherTabs({ type: 'phone-disconnected' });
      }
    },
    [broadcastToOtherTabs, unregisterDevice]
  );

  const refreshConferenceSession = useCallback(async () => {
    if (conferenceRefreshInFlightRef.current) {
      return;
    }

    conferenceRefreshInFlightRef.current = true;
    const refreshSeq = ++conferenceRefreshSeqRef.current;

    try {
      const primarySid = getActiveCallLegSid();
      if (!primarySid) {
        return;
      }

      const session = conferenceSessionRef.current;
      const profile = medplum.getProfile();
      const practitionerRef = profile ? getReferenceString(createReference(profile)) : undefined;
      const viewerIsHost =
        session?.participants.some(
          (p) => p.role === ConferenceParticipantRole.AGENT && p.providerReference === practitionerRef
        ) ?? isOutgoingCall;
      const cachedParentSid = viewerIsHost ? session?.parentCallSid : undefined;
      const sidsToTry = [
        ...new Set([primarySid, ...(cachedParentSid && cachedParentSid !== primarySid ? [cachedParentSid] : [])]),
      ];

      let lastError: string | undefined;
      for (const sid of sidsToTry) {
        const r = await fetchBot<ConferenceControlResult>(medplum, 'twilio-conference-control', {
          action: ConferenceControlAction.GET_STATUS,
          parentCallSid: sid,
        });

        if (refreshSeq !== conferenceRefreshSeqRef.current) {
          return;
        }

        if (!r.error) {
          setConferenceSessionError(null);
          setConferenceSession(r.session ?? null);
          setCanManageConference(r.canManageConference ?? false);
          return;
        }

        lastError = r.error;
      }

      if (refreshSeq !== conferenceRefreshSeqRef.current) {
        return;
      }

      const hadValidSession = conferenceSessionRef.current != null;
      const suppressLookupError =
        (lastError === 'Call not found' && (isWithinPostAnswerGrace(callAnsweredAtRef.current) || hadValidSession)) ||
        (hadValidSession && isTransientConferencePollingError(lastError));

      if (!suppressLookupError) {
        setConferenceSessionError(lastError ?? null);
        setConferenceSession(null);
        setCanManageConference(false);
      }
    } catch (e) {
      if (refreshSeq !== conferenceRefreshSeqRef.current) {
        return;
      }

      const hadValidSession = conferenceSessionRef.current != null;
      const message = e instanceof Error ? e.message : 'Conference status failed';
      if (!hadValidSession && !isTransientConferencePollingError(message)) {
        setConferenceSessionError(message);
        setConferenceSession(null);
        setCanManageConference(false);
      }
    } finally {
      conferenceRefreshInFlightRef.current = false;
    }
  }, [medplum, getActiveCallLegSid, isOutgoingCall]);

  const mergeConferenceCall = useCallback(async () => {
    const sid = getActiveCallLegSid();
    if (!sid) {
      alert.error('Could not identify your call leg. Try again in a moment.');
      return;
    }
    try {
      const r = await fetchBot<ConferenceControlResult>(medplum, 'twilio-conference-control', {
        action: ConferenceControlAction.MERGE,
        parentCallSid: sid,
      });
      if (r.error) {
        alert.error(r.error);
      } else if (r.session) {
        setConferenceSession(r.session);
        setConferenceSessionError(null);
        setCanManageConference(r.canManageConference ?? true);
      }
    } catch (e) {
      alert.error(e instanceof Error ? e.message : 'Merge failed');
    }
  }, [medplum, getActiveCallLegSid]);

  const cancelConferenceConsult = useCallback(async () => {
    const sid = getActiveCallLegSid();
    if (!sid) {
      alert.error('Could not identify your call leg. Try again in a moment.');
      return;
    }
    try {
      const r = await fetchBot<ConferenceControlResult>(medplum, 'twilio-conference-control', {
        action: ConferenceControlAction.CANCEL_CONSULT,
        parentCallSid: sid,
      });
      if (r.error) {
        alert.error(r.error);
      } else if (r.session) {
        setConferenceSession(r.session);
        setConferenceSessionError(null);
        setCanManageConference(r.canManageConference ?? true);
      }
    } catch (e) {
      alert.error(e instanceof Error ? e.message : 'Could not cancel consult');
    }
  }, [medplum, getActiveCallLegSid]);

  const reconnectPrimaryConference = useCallback(async () => {
    const sid = getActiveCallLegSid();
    if (!sid) {
      return;
    }
    try {
      const r = await fetchBot<ConferenceControlResult>(medplum, 'twilio-conference-control', {
        action: ConferenceControlAction.RECONNECT_PRIMARY,
        parentCallSid: sid,
      });
      if (r.error) {
        alert.error(r.error);
      } else if (r.session) {
        setConferenceSession(r.session);
        setConferenceSessionError(null);
        setCanManageConference(r.canManageConference ?? true);
        patientLeftNotifiedRef.current = false;
      }
    } catch (e) {
      alert.error(e instanceof Error ? e.message : 'Could not call patient back');
    }
  }, [medplum, getActiveCallLegSid]);

  useEffect(() => {
    if (!isMultiPartyCallingEnabled) {
      return;
    }

    const hasActiveCall = Boolean(currentCall || activeCallUi);
    if (!hasActiveCall) {
      setConferenceSession(null);
      setConferenceSessionError(null);
      setCanManageConference(false);
      return;
    }

    void refreshConferenceSession();
    const id = window.setInterval(() => void refreshConferenceSession(), 5000);

    return () => clearInterval(id);
  }, [currentCall, activeCallUi, refreshConferenceSession, isMultiPartyCallingEnabled]);

  const startConferenceAdd = useCallback(
    async (target: ConferenceStartAddTarget) => {
      const sid = getActiveCallLegSid();
      if (!sid) {
        alert.error('Could not identify your call leg. Try again in a moment.');
        return;
      }
      const payload =
        target.type === 'phone'
          ? { action: ConferenceControlAction.START_ADD, parentCallSid: sid, toPhone: target.toPhone }
          : {
              action: ConferenceControlAction.START_ADD,
              parentCallSid: sid,
              providerReference: target.providerReference,
            };
      try {
        const r = await fetchBot<ConferenceControlResult>(medplum, 'twilio-conference-control', payload);
        if (r.error) {
          if (isTransientConferenceActionError(r.error)) {
            void refreshConferenceSession();
          } else {
            alert.error(r.error);
          }
        } else if (r.session) {
          setConferenceSession(r.session);
          setConferenceSessionError(null);
          setCanManageConference(r.canManageConference ?? true);
        } else {
          void refreshConferenceSession();
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Could not add caller';
        if (isTransientConferenceActionError(message)) {
          void refreshConferenceSession();
        } else {
          alert.error(message);
        }
      }
    },
    [medplum, getActiveCallLegSid, refreshConferenceSession]
  );

  const dropConferenceParticipant = useCallback(
    async (participantCallSid: string) => {
      const sid = getActiveCallLegSid();
      if (!sid) {
        alert.error('Could not identify your call leg. Try again in a moment.');
        return;
      }
      try {
        const r = await fetchBot<ConferenceControlResult>(medplum, 'twilio-conference-control', {
          action: ConferenceControlAction.DROP_PARTICIPANT,
          parentCallSid: sid,
          participantCallSid,
        });
        if (r.error) {
          alert.error(r.error);
        } else if (r.session) {
          setConferenceSession(r.session);
          setConferenceSessionError(null);
          setCanManageConference(r.canManageConference ?? true);
        }
      } catch (e) {
        alert.error(e instanceof Error ? e.message : 'Remove failed');
      }
    },
    [medplum, getActiveCallLegSid]
  );

  useEffect(() => {
    let newDevice: Device | null = null;

    const setupDeviceOnThisTab = async () => {
      if (!medplum.isAuthenticated()) return;

      try {
        const tokenResponse = await getTwilioToken(medplum);
        setAvailableCallerIds(tokenResponse.availableCallerIds);
        newDevice = await setupDevice({
          token: tokenResponse.token,
          medplum,
          onRegistered: async () => {
            console.log('Device registered');
          },
          onUnregistered: async () => {
            console.log('Device unregistered');
            resetState();
          },
          onError: (error) => {
            console.log('Device error', error);
          },
          onIncoming: async (conn: any) => {
            // Once the call stops ringing here, its push must go too (BAR-2531). Attached before the
            // caller-id lookup below: a cancel that lands while it is in flight would otherwise find
            // no listener, and the push's Accept would answer whatever rings in this tab next.
            const closeRingPush = () => void closeIncomingCallNotifications(resolveStableCallLegSid(conn));
            conn.on('accept', closeRingPush);
            conn.on('cancel', closeRingPush);
            conn.on('reject', closeRingPush);

            // For incoming calls, the user might not have direct access to the patient so we need to
            // get the caller info from the backend in order to display the caller name
            const { callerName, callerOrganization } = await fetchBot<{
              callerName: string;
              callerOrganization: string;
            }>(medplum, 'caller-id', { phoneNumber: conn?.parameters?.From });

            console.log('Device incoming call');

            conn.on('accept', function () {
              console.log('Incoming call accepted (SDK accept event)');
              const answeredAt = Date.now();
              callAnsweredAtRef.current = answeredAt;
              currentCallRef.current = conn;
              setCurrentCall(conn);
              setIncomingCall(null);
              setIsRemotePartyConnected(true);
              const legSid = resolveStableCallLegSid(conn);
              if (legSid) {
                callLegSidRef.current = legSid;
              }
            });

            conn.on('cancel', function () {
              console.log('Call was canceled before being answered');
              if (currentCallRef.current === conn) {
                resetState();
                broadcastToOtherTabs({ type: 'call-disconnected' });
                return;
              }
              setIncomingCall((inc) => (inc?.connection === conn ? null : inc));
              setCurrentCall((c) => (c === conn ? null : c));
            });

            conn.on('reject', function () {
              console.log('Call was rejected');
              if (currentCallRef.current === conn && callAnsweredAtRef.current != null) {
                return;
              }
              resetState();
              broadcastToOtherTabs({ type: 'call-rejected' });
            });

            conn.on('disconnect', function () {
              console.log('Call has been disconnected');
              const legSid = conn.parameters?.CallSid as string | undefined;
              const wasActiveCall =
                currentCallRef.current === conn ||
                (legSid != null && callLegSidRef.current === legSid && callAnsweredAtRef.current != null);
              const answeredAt = callAnsweredAtRef.current;

              if (wasActiveCall && isWithinPostAnswerGrace(answeredAt)) {
                console.log('Ignoring spurious disconnect during post-answer grace');
                return;
              }

              if (wasActiveCall && answeredAt != null) {
                resetState();
                broadcastToOtherTabs({ type: 'call-disconnected' });
                return;
              }

              setIncomingCall((inc) => (inc?.connection === conn ? null : inc));
              setCurrentCall((c) => (c === conn ? null : c));
            });

            conn.on('error', function (error: any) {
              // Handle Twilio call errors gracefully
              // These are often service-level issues (network, gateway errors) that are expected
              console.warn('Incoming call error occurred:', error);
              // Don't reset state immediately on error - let disconnect/cancel handle cleanup
            });

            // Call quality monitoring for debugging one-way audio issues
            conn.on('warning', function (warningName: string, warningData: unknown) {
              reportCallWarning(conn, warningName, warningData, 'incoming');
            });

            conn.on('warning-cleared', function (warningName: string) {
              console.log('Incoming call warning cleared:', warningName);
            });

            setIsOutgoingCall(false);
            setIncomingCall({
              connection: conn,
              from: conn.parameters.From || 'Unknown Number',
              callerName,
              callerOrganization,
            });
          },
        });
        setDevice(newDevice);
      } catch (error) {
        console.error('Failed to auto-connect device:', error);
      }
    };

    setupDeviceOnThisTab();

    return () => {
      if (!newDevice) return;
      newDevice.disconnectAll();
      if (newDevice.state !== Device.State.Unregistered) {
        newDevice.unregister();
      }
      if (newDevice.state !== Device.State.Destroyed) {
        newDevice.destroy();
      }
      setDevice(null);
    };
  }, [medplum, broadcastToOtherTabs, resetState]);

  // Initialize BroadcastChannel for cross-tab communication
  useEffect(() => {
    const handleCrossTabMessage = async (event: MessageEvent<CrossTabMessage>) => {
      const { type, payload, tabId } = event.data;

      if (tabId === tabIdRef.current) {
        return;
      }

      if (currentCallRef.current) {
        console.log('Received a broadcast while in a call, ignoring it. Type:', type);
        return;
      }

      switch (type) {
        case 'phone-connected':
          console.log('Received broadcast: phone-connected', payload);
          hasReceivedPhoneStatusFromOtherTabsAtRef.current = true;
          connect(false);
          break;

        case 'phone-disconnected':
          console.log('Received broadcast: phone-disconnected', payload);
          hasReceivedPhoneStatusFromOtherTabsAtRef.current = true;
          disconnect(false);
          break;

        case 'call-answered':
          console.log('Received broadcast: call-answered', payload);
          setIncomingCall(null);
          setCallStartTime(payload?.callStartTime ? new Date(payload.callStartTime) : new Date());
          setIsOutgoingCall(payload?.isOutgoing ?? false);
          setIsInCallFromOtherTab(true);
          break;

        case 'call-rejected':
          console.log('Received broadcast: call-rejected', payload);
          resetState();
          break;

        case 'call-disconnected':
          console.log('Received broadcast: call-disconnected', payload);
          resetState();
          break;

        case 'making-call':
          console.log('Received broadcast: call-started', payload);
          setIncomingCall(null);
          setCallStartTime(payload?.callStartTime ? new Date(payload.callStartTime) : new Date());
          setIsOutgoingCall(payload?.isOutgoing ?? true);
          setIsInCallFromOtherTab(true);
          break;

        case 'request-phone-status':
          console.log('Received broadcast: request-phone-status');
          if (device && device.state === Device.State.Registered) {
            console.log('Responding to phone status request - phone is connected');
            broadcastToOtherTabs({ type: 'phone-connected' });
          } else {
            console.log('Responding to phone status request - phone is not connected');
            broadcastToOtherTabs({ type: 'phone-disconnected' });
          }
          break;
      }
    };

    broadcastChannelRef.current = new BroadcastChannel('twilio-phone-state');
    broadcastChannelRef.current?.addEventListener('message', handleCrossTabMessage);

    // On tab load, request phone status from other tabs
    if (!hasRequestedInitialPhoneStatusAtRef.current && device) {
      console.log('Tab loaded, requesting phone status from other tabs');
      broadcastToOtherTabs({ type: 'request-phone-status' });
      hasRequestedInitialPhoneStatusAtRef.current = true;

      setTimeout(() => {
        if (!hasReceivedPhoneStatusFromOtherTabsAtRef.current) {
          console.log('No phone status received from other tabs, connecting this tab');
          connect(false);
        }
      }, 1000);
    }

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.removeEventListener('message', handleCrossTabMessage);
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [medplum, device, broadcastToOtherTabs, connect, disconnect, resetState]);

  // Call controls
  const makeCall = useCallback(
    async (phoneNumber: string, callerId?: string, recipient?: string) => {
      if (!phoneNumber.trim()) {
        console.log('makeCall: No phone number provided');
        return;
      }

      console.log('makeCall: Attempting to call', phoneNumber);
      console.log('makeCall: Device state:', device?.state);
      console.log('makeCall: currentCall:', currentCall);
      console.log('makeCall: callerId:', callerId);

      // Check if we're already in a call
      if (currentCall || isInCallFromOtherTab) {
        console.log('makeCall: Already in a call, cannot make another call');
        return;
      }

      // Handle unregistered device by attempting to reconnect
      // Use the returned device object to avoid closure variable issues
      let activeDevice = device;
      if (!activeDevice || activeDevice.state !== Device.State.Registered) {
        console.log('makeCall: Device not connected. Attempting to reconnect...');
        activeDevice = await reconnectDeviceWithTimeout();
        if (!activeDevice || activeDevice.state !== Device.State.Registered) {
          return;
        }
      }

      try {
        outgoingHangupInitiatedLocallyRef.current = false;
        console.log('makeCall: Making outgoing call to:', phoneNumber, 'with caller ID:', callerId);

        const startTime = new Date();
        setIsOutgoingCall(true);
        setIsRemotePartyConnected(false);
        setCallStartTime(startTime);

        broadcastToOtherTabs({
          type: 'making-call',
          payload: {
            isOutgoing: true,
            callStartTime: startTime.toISOString(),
          },
        });

        console.log('makeCall: About to call device.connect()');

        const params: Record<string, string> = {
          To: phoneNumber,
        };

        // Add caller ID if provided
        if (callerId) {
          params.CallerId = callerId;
        }

        // Add recipient if provided (for explicit Patient or Organization reference)
        if (recipient) {
          params.Recipient = recipient;
        }

        const conn = await activeDevice.connect({ params });

        console.log('makeCall: Call initiated, setting up event handlers');

        // Dedupe toast for this outbound call. (Twilio can fire multiple end events.)
        let outboundCallToastShown = false;
        const showOutboundCallToast = (message: string) => {
          if (outboundCallToastShown) return;
          outboundCallToastShown = true;
          alert.warning(message);
        };

        const parseBase64Json = (data: string): any | undefined => {
          try {
            // Medplum stores Attachment.data as base64.
            // At runtime in the browser, `atob` is available.
            return JSON.parse(atob(data));
          } catch {
            return undefined;
          }
        };

        const getLatestVoiceStatusPayload = (communication: Communication): any | undefined => {
          const history = getExtension(communication, TWILIO_CALL_HISTORY_EXTENSION)?.extension ?? [];
          const voiceStatusEntries = history.filter((e) => e.url === 'voice-status');
          const latest = voiceStatusEntries[voiceStatusEntries.length - 1];
          const data = latest?.valueAttachment?.data;
          return data ? parseBase64Json(data) : undefined;
        };

        const showOutboundDialOutcomeToastIfPresent = async (): Promise<void> => {
          if (outboundCallToastShown) return;
          if (outgoingHangupInitiatedLocallyRef.current) return;

          const callSid = conn.parameters?.CallSid;
          if (!callSid) return;

          let communications: Communication[] = [];
          try {
            communications = (await medplum.searchResources('Communication', {
              identifier: `${TWILIO_SYSTEM}|${callSid}`,
            })) as Communication[];
          } catch (err) {
            console.warn('Failed to fetch call Communication for outbound outcome:', err);
            return;
          }

          for (const comm of communications) {
            const payload = getLatestVoiceStatusPayload(comm);
            const dialCallStatus = payload?.DialCallStatus as string | undefined;
            const dialCallStatusMessage = payload?.dialCallStatusMessage as string | undefined;

            if (!dialCallStatus) continue;

            // These are the outcomes handled by `twilio-voice-outgoing-action-callback`.
            const defaultMessage =
              TWILIO_DIAL_CALL_STATUS_MESSAGE[dialCallStatus as keyof typeof TWILIO_DIAL_CALL_STATUS_MESSAGE];
            if (defaultMessage) {
              showOutboundCallToast(dialCallStatusMessage ?? defaultMessage);
              return;
            }
          }
        };

        // IMPORTANT: Set up ALL event handlers IMMEDIATELY after connect returns
        // to avoid race conditions where events fire before handlers are registered.
        // This must happen BEFORE any async operations like getCallInfoByPhone.

        conn.on('accept', function () {
          console.log('makeCall: Call accepted by remote party, audio path open');
          setIsRemotePartyConnected(true);
        });

        conn.on('ringing', function (hasEarlyMedia: boolean) {
          console.log('makeCall: Remote phone is ringing, hasEarlyMedia:', hasEarlyMedia);
        });

        conn.on('disconnect', function () {
          outgoingHangupInitiatedLocallyRef.current = false;
          void showOutboundDialOutcomeToastIfPresent();
          console.log('Call has been disconnected');
          resetState();
          broadcastToOtherTabs({ type: 'call-disconnected' });
        });

        conn.on('cancel', function () {
          outgoingHangupInitiatedLocallyRef.current = false;
          void showOutboundDialOutcomeToastIfPresent();
          console.log('Call was canceled before being answered');
          resetState();
          broadcastToOtherTabs({ type: 'call-disconnected' });
        });

        conn.on('reject', function () {
          outgoingHangupInitiatedLocallyRef.current = false;
          void showOutboundDialOutcomeToastIfPresent();
          console.log('Call was rejected');
          resetState();
          broadcastToOtherTabs({ type: 'call-rejected' });
        });

        conn.on('error', function (error: any) {
          console.warn('Call error occurred:', error);
        });

        // Call quality monitoring for debugging one-way audio issues
        conn.on('warning', function (warningName: string, warningData: unknown) {
          reportCallWarning(conn, warningName, warningData, 'outgoing');
        });

        conn.on('warning-cleared', function (warningName: string) {
          console.log('Outgoing call warning cleared:', warningName);
        });

        // Set current call immediately so audio can flow
        const legSid = conn.parameters?.CallSid as string | undefined;
        if (legSid) {
          callLegSidRef.current = legSid;
        }

        setCurrentCall(conn);
        setIsInCallFromOtherTab(false);

        // Fetch caller info AFTER event handlers are set up
        // This prevents race conditions where events fire before we're listening
        const { callerName, callerOrganization } = await getCallInfoByPhone(medplum, phoneNumber);

        setOutgoingCall({
          connection: conn,
          to: phoneNumber,
          callerName,
          callerOrganization,
        });

        setActiveCallUi({
          direction: 'outgoing',
          phoneNumber,
          callerName,
          callerOrganization,
          callLegSid: legSid,
        });

        console.log('makeCall: Call setup completed successfully');
      } catch (error) {
        console.error('makeCall: Failed to make outgoing call:', error);
        alert.error(`Couldn't start the call. ${error instanceof Error ? error.message : 'Please try again.'}`);
        setIsOutgoingCall(false);
        setCallStartTime(null);
        broadcastToOtherTabs({ type: 'call-disconnected' });
      }
    },
    [device, broadcastToOtherTabs, currentCall, isInCallFromOtherTab, resetState, medplum, reconnectDeviceWithTimeout]
  );

  const acceptCall = useCallback(
    async (options?: CallNotificationActionOptions) => {
      console.log('Call accepted');

      if (!incomingCall || isStaleNotificationAction(options?.notificationCallSid, incomingCall)) return;

      const conn = incomingCall.connection;
      const from = incomingCall.from;
      const callerName = incomingCall.callerName;
      const callerOrganization = incomingCall.callerOrganization;
      const startTime = new Date();
      const legSid = resolveStableCallLegSid(conn);

      callAnsweredAtRef.current = Date.now();
      currentCallRef.current = conn;
      if (legSid) {
        callLegSidRef.current = legSid;
      }

      conn.accept();

      setCallStartTime(startTime);
      setCurrentCall(conn);
      setIncomingCall(null);
      setIsInCallFromOtherTab(false);
      setIsRemotePartyConnected(true);
      setActiveCallUi({
        direction: 'incoming',
        phoneNumber: from,
        callerName,
        callerOrganization,
        callLegSid: legSid,
      });

      // Notify other tabs that call was answered
      broadcastToOtherTabs({
        type: 'call-answered',
        payload: {
          from,
          isOutgoing: false,
          callStartTime: startTime.toISOString(),
        },
      });
    },
    [incomingCall, broadcastToOtherTabs]
  );

  const rejectCall = useCallback(
    async (options?: CallNotificationActionOptions) => {
      console.log('Call rejected');

      if (isStaleNotificationAction(options?.notificationCallSid, incomingCall)) return;

      incomingCall?.connection?.reject();
      setIncomingCall(null);
      setCurrentCall(null);
      setActiveCallUi(null);
      setIsOutgoingCall(false);
      setCallStartTime(null);
      setIsInCallFromOtherTab(false);
      setIsRemotePartyConnected(false);
      callAnsweredAtRef.current = null;
      callLegSidRef.current = null;
      currentCallRef.current = null;
    },
    [incomingCall]
  );

  const hangupCall = useCallback(async () => {
    console.log('Call hung up');

    if (!currentCall && !activeCallUi) return;

    if (currentCall) {
      if (isOutgoingCall) {
        outgoingHangupInitiatedLocallyRef.current = true;
        console.log('[Twilio outbound] hangupCall: marked localHangup=true (provider ended outgoing call)');
      } else {
        console.log('[Twilio outbound] hangupCall: incoming or unknown — localHangup ref not set', {
          isOutgoingCall,
        });
      }
      currentCall.disconnect();
    } else if (device) {
      device.disconnectAll();
    }

    resetState();
    broadcastToOtherTabs({ type: 'call-disconnected' });
  }, [currentCall, activeCallUi, isOutgoingCall, device, resetState, broadcastToOtherTabs]);

  const connectionStatus = useMemo((): 'offline' | 'connecting' | 'online' | 'in-call' => {
    if (currentCall || isInCallFromOtherTab || activeCallUi) {
      return 'in-call';
    }

    if (device?.state === Device.State.Registered) {
      return 'online';
    }

    if (device?.state === Device.State.Registering) {
      return 'connecting';
    }

    if (!refreshConnectionStatus) {
      // This is a hack to force a refresh of the connection status
      // this if statement is here just to prevent linting errors
      // on the useMemo hook dependencies
    }

    return 'offline';
  }, [device, currentCall, isInCallFromOtherTab, activeCallUi, refreshConnectionStatus]);

  // Timer logic for live call duration updates
  const [timerUpdate, setTimerUpdate] = useReducer((t: number) => t + 1, 0);
  const callDurationTimer = useInterval(() => connectionStatus === 'in-call' && setTimerUpdate(), 1000);

  const callDuration = useMemo(() => {
    if (!callStartTime || timerUpdate === 0) return '';

    const duration = Math.floor((Date.now() - callStartTime.getTime()) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [callStartTime, timerUpdate]);

  // Manage call duration timer lifecycle
  useEffect(() => {
    if (connectionStatus === 'in-call') {
      callDurationTimer.start();
    } else {
      callDurationTimer.stop();
    }

    return () => callDurationTimer.stop();
  }, [connectionStatus, callDurationTimer]);

  useEffect(() => {
    if (currentCall && isCallLegClosed(currentCall)) {
      if (isWithinPostAnswerGrace(callAnsweredAtRef.current)) {
        console.log('Ignoring closed call leg during post-answer grace');
        return;
      }
      resetState();
      return;
    }
    if (incomingCall?.connection && isCallLegClosed(incomingCall.connection)) {
      setIncomingCall(null);
    }
    if (outgoingCall?.connection && isCallLegClosed(outgoingCall.connection)) {
      setOutgoingCall(null);
    }
  }, [currentCall, incomingCall, outgoingCall, resetState]);

  // Handle tab visibility changes to proactively reconnect device
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When tab becomes visible, check if device needs reconnection
      if (!document.hidden && device && device.state === Device.State.Unregistered) {
        console.log('Tab became visible, device is unregistered. Reconnecting...');
        void registerDevice().catch((err) => console.warn('Visibility re-register failed:', err));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [device, registerDevice]);

  return (
    <TwilioPhoneContext.Provider
      value={{
        connectionStatus,
        currentCall,
        incomingCall,
        outgoingCall,
        isOutgoingCall,
        callStartTime,
        isInCallFromOtherTab,
        availableCallerIds,
        conferenceSession,
        conferenceSessionError,
        canManageConference,
        activeCallUi,
        isRemotePartyConnected,

        // Connection controls
        connect,
        disconnect,

        // Call controls
        makeCall,
        acceptCall,
        rejectCall,
        hangupCall,

        // Conference (multi-party)
        refreshConferenceSession,
        startConferenceAdd,
        mergeConferenceCall,
        cancelConferenceConsult,
        reconnectPrimaryConference,
        dropConferenceParticipant,

        // Utility
        callDuration,
      }}
    >
      {children}
    </TwilioPhoneContext.Provider>
  );
}
